import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { getInvoice } from "../../api/password";
import { Col, Row, Typography, Spin, Divider } from "antd";
import logo from "../../assets/images/my-friend-logo-blue.svg";
import downloadIcon from "../../assets/images/Download.svg";
import printIcon from "../../assets/images/Print.svg";
import { Countries, getCurrencySymbol, countryCurrencyMap } from '../../constants/constants.js';

const { Title, Text } = Typography;

const InvoicePage = () => {
    const location = useLocation();
    const { stripePaymentIntentId } = location.state || {};
    const [invoiceData, setInvoiceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (stripePaymentIntentId) {
            const fetchInvoice = async () => {
                try {
                    const response = await getInvoice(stripePaymentIntentId);
                    setInvoiceData(response);
                } catch (err) {
                    console.error("Error fetching invoice data:", err);
                    setError(err);
                } finally {
                    setLoading(false);
                }
            };

            fetchInvoice();
        }
    }, [stripePaymentIntentId]);

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    if (error) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Text type="danger">Failed to load invoice data</Text></div>;
    }

    const boldDividerStyle = {
        borderColor: '#000',
        borderWidth: '1px',
    };

    const getCountryLabel = (countryValue) => {
        const country = Countries.find(c => c.value === countryValue);
        return country ? country.label : 'Not Found';
    };

    const countryLabel = getCountryLabel(invoiceData.invoicedTo.address.country);
    // const currencyCode = countryCurrencyMap[invoiceData.invoicedTo.address.country];
    // const currencySymbol = getCurrencySymbol(currencyCode);

    const handleDownloadInvoice = async () => {

        const formattedText = `
                Invoice #${invoiceData.invoiceNumber}
                Created: ${invoiceData.created}
                
                INVOICED TO
                Name: ${invoiceData.invoicedTo.name}
                Country: ${countryLabel}
                City: ${invoiceData.invoicedTo.address.city}
                Address: ${invoiceData.invoicedTo.address.line1}
                Postal Code: ${invoiceData.invoicedTo.address.postal_code}
                
                SUBSCRIPTION
                Subscription ID: ${invoiceData.subscription.subscriptionId}
                Billing Period: ${invoiceData.subscription.billingPeriod}
                Next Billing Date: ${invoiceData.subscription.nextBillingDate}
                
                DESCRIPTION
                Gross Total: £ ${invoiceData.payments.grossTotal}
                Amount Paid: -£ ${invoiceData.payments.amountPaid}
                Amount Due: £ ${invoiceData.payments.amountDue}

                VAT DETAILS
                Total Excluding VAT: £ ${invoiceData.vatDetails.totalExclVat}
                VAT Amount: £ ${invoiceData.vatDetails.vatAmount}
                VAT Percentage: ${invoiceData.vatDetails.vatPercentage}%
                Processing Fee: £ ${invoiceData.vatDetails.processingFee}
                
                VAT DETAILS
                Total (excl. VAT): £ ${invoiceData.vatDetails.totalExclVat}
                VAT ${invoiceData.vatDetails.vatRule}: £ ${invoiceData.vatDetails.vatAmount}
                ${invoiceData.vatDetails.processingFee !== "Yearly Plan" ? `Processing Fee: £ ${invoiceData.vatDetails.processingFee}` : ''}

                PAID TO
                Strategia Data Sciences
                Level 39, One Canada Square
                Canary Wharf, London, E14 5AB
                United Kingdom
                VAT Number: GB433071723
            `;

        const element = document.createElement("a");
        const file = new Blob([formattedText], { type: "text/plain" });
        element.href = URL.createObjectURL(file);
        element.download = `invoice_${invoiceData.invoiceNumber}.txt`;
        document.body.appendChild(element);
        element.click();
        document.body.removeChild(element);

    };

    const handlePrintInvoice = async () => {

        const formattedText = `
                <h1>Invoice #${invoiceData.invoiceNumber}</h1>
                <p>Created: ${invoiceData.created}</p>
                
                <h2>INVOICED TO</h2>
                <p>Name: ${invoiceData.invoicedTo.name}</p>
                <p>Country: ${countryLabel}</p>
                <p>City: ${invoiceData.invoicedTo.address.city}</p>
                <p>Address: ${invoiceData.invoicedTo.address.line1}</p>
                <p>Postal Code: ${invoiceData.invoicedTo.address.postal_code}</p>
                
                <h2>SUBSCRIPTION</h2>
                <p>Subscription ID: ${invoiceData.subscription.subscriptionId}</p>
                <p>Billing Period: ${invoiceData.subscription.billingPeriod}</p>
                <p>Next Billing Date: ${invoiceData.subscription.nextBillingDate}</p>
                
                <h2>DESCRIPTION</h2>
                <p>Gross Total: £${invoiceData.payments.grossTotal}</p>
                <p>Amount Paid: -£${invoiceData.payments.amountPaid}</p>
                <p>Amount Due: £${invoiceData.payments.amountDue}</p>

                <h2>VAT DETAILS</h2>
                <p>Total Excluding VAT: £${invoiceData.vatDetails.totalExclVat}</p>
                <p>VAT ${invoiceData.vatDetails.vatRule}: £${invoiceData.vatDetails.vatAmount}</p>
                ${invoiceData.vatDetails.processingFee !== "Yearly Plan" ? `<p>Processing Fee: £${invoiceData.vatDetails.processingFee}</p>` : ''}

                <h2>PAID TO</h2>
                <p>Strategia Data Sciences</p>
                <p>Level 39, One Canada Square</p>
                <p>Canary Wharf, London, E14 5AB</p>
                <p>United Kingdom</p>
                <p>VAT Number: GB433071723</p>
            `;

        const printWindow = window.open("", "", "height=600,width=800");
        printWindow.document.write(`
                <html>
                <head>
                    <title>Print Invoice</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; }
                        pre { white-space: pre-wrap; }
                    </style>
                </head>
                <body>
                    ${formattedText}
                    <script>
                        window.onload = function() {
                            window.print();
                            window.onafterprint = function() {
                                window.close();
                            }
                        }
                    </script>
                </body>
                </html>
            `);

        printWindow.document.close();
        printWindow.focus();
    };

    return (
        <div style={{ display: 'flex', paddingTop: '10px', paddingBottom: '10px', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#fefcfe' }}>
            <div style={{ border: '2px solid black', padding: '40px', borderRadius: '8px', width: '80%', maxWidth: '600px', background: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <img src={logo} alt="Logo" style={{ maxWidth: '300px' }} />
                    <img src={downloadIcon} alt="Logo" style={{ maxWidth: '400px', marginLeft: "60px" }} onClick={() => handleDownloadInvoice()} />
                    <img src={printIcon} alt="Logo" style={{ maxWidth: '300px' }} onClick={() => handlePrintInvoice()} />
                    <div style={{ backgroundColor: '#78A76B', color: '#fff', padding: '5px 10px', borderRadius: '4px', fontWeight: 'bold' }}>
                        PAID
                    </div>
                </div>
                {invoiceData ? (
                    <div style={{ width: '100%' }}>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Title level={4}>Invoice #{invoiceData.invoiceNumber}</Title>
                            </Col>
                            <Col span={24}>
                                <Text>{invoiceData.created}</Text>
                            </Col>
                        </Row>
                        <Divider style={boldDividerStyle} />

                        <Title level={5}>INVOICED TO</Title>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Text strong>Name:</Text> <Text>{invoiceData.invoicedTo.name}</Text>
                            </Col>
                            <Col span={24}>
                                {countryLabel !== 'Not Found' && (
                                    <>
                                        <Text strong>Country:</Text> <Text>{countryLabel}</Text><br />
                                    </>
                                )}
                                {invoiceData.invoicedTo.address.city !== 'Not Found' && (
                                    <>
                                        <Text strong>City: </Text><Text>{invoiceData.invoicedTo.address.city} </Text><br />
                                    </>
                                )}
                                {invoiceData.invoicedTo.address.line1 !== 'Not Found' && (
                                    <>
                                        <Text strong>Address:</Text> <Text>{invoiceData.invoicedTo.address.line1}</Text><br />
                                    </>
                                )}
                                {invoiceData.invoicedTo.address.postal_code !== 'Not Found' && (
                                    <>
                                        <Text strong>Postal Code:</Text> <Text>{invoiceData.invoicedTo.address.postal_code}</Text><br />
                                    </>
                                )}
                            </Col>
                        </Row>
                        <Divider style={boldDividerStyle} />

                        <Title level={5}>SUBSCRIPTION</Title>
                        <Row gutter={16}>
                            <Col span={24}>
                                <Text strong>Subscription ID:</Text> <Text>{invoiceData.subscription.subscriptionId}</Text>
                            </Col>
                            <Col span={24}>
                                <Text strong>Billing Period:</Text> <Text>{invoiceData.subscription.billingPeriod}</Text>
                            </Col>
                            <Col span={24}>
                                <Text strong>Next Billing Date:</Text> <Text>{invoiceData.subscription.nextBillingDate}</Text>
                            </Col>
                        </Row>
                        <Divider style={boldDividerStyle} />

                        <Row gutter={16}>
                            <Col span={16}>
                                <Title level={5}>Description</Title>
                            </Col>
                            <Col span={8} style={{ textAlign: 'right' }}>
                                <Text strong>Amount (GBP):</Text>
                            </Col>
                        </Row>
                        <Divider style={boldDividerStyle} />

                        <Row gutter={16}>
                            <Col span={16} />
                            <Col span={8} style={{ textAlign: 'right' }}>
                                <Text strong>Gross Total:</Text> <Text>£ {invoiceData.payments.grossTotal}</Text><br />
                                <Text strong>Amount Paid:</Text> <Text>-£ {invoiceData.payments.amountPaid}</Text><br />
                                <Text strong>Amount Due:</Text> <Text>£ {invoiceData.payments.amountDue}</Text>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={16}>
                                <Title level={5}>VAT DETAILS</Title>
                            </Col>
                            <Col span={24} style={{ textAlign: 'left' }}>
                                <Text strong>Total (excl. VAT) : </Text> <Text>£{invoiceData.vatDetails.totalExclVat}</Text><br />
                                <Text strong>VAT {invoiceData.vatDetails.vatRule} : </Text> <Text>£{invoiceData.vatDetails.vatAmount}</Text><br />
                                {invoiceData.vatDetails.processingFee !== "Yearly Plan" && (
                                    <>
                                        <Text strong>Processing Fee:</Text> <Text>£ {invoiceData.vatDetails.processingFee}</Text>
                                    </>
                                )}
                            </Col>
                        </Row>

                        <Divider style={boldDividerStyle} />

                        <div style={{ textAlign: 'right' }}>
                            <Title level={5}>PAID TO</Title>
                            <Text>Strategia Data Sciences</Text><br />
                            <Text>Level 39, One Canada Square</Text><br />
                            <Text>Canary Wharf, London, E14 5AB</Text><br />
                            <Text>United Kingdom</Text><br />
                            <Text>VAT Number: GB433071723</Text>
                        </div>
                    </div>
                ) : (
                    <Text>No invoice data available</Text>
                )}

            </div>
        </div>
    );
};

export default InvoicePage;
